import {
	ApolloClient,
	ApolloLink,
	NetworkStatus as ApolloNetworkStatus,
	createHttpLink,
	from,
	gql as gqlFN,
	InMemoryCache,
	type NormalizedCacheObject,
	Observable,
	useApolloClient as useApolloClientFn,
	useQuery as useApolloQuery,
	useLazyQuery as useLazyQueryFn,
	useMutation as useMutationFn,
	useSuspenseQuery as useSuspenseQueryFn,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';

import { fetchAuthSession } from '../Amplify';
import { ApolloContext, Cookie } from '../types';

export const useApolloClient = useApolloClientFn;
const removeTypenameLink = removeTypenameFromVariables();

const errorLink = onError(({ graphQLErrors, networkError }) => {
	if (graphQLErrors) {
		graphQLErrors.forEach(({ message, locations, path }) => {
			console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
		});
	}
	if (networkError) console.error(`[Network error]: ${networkError}`);
});

export const createApolloClient = (
	context: ApolloContext,
	ssr: boolean = true,
): ApolloClient<NormalizedCacheObject> => {
	const httpLinkV1 = createHttpLink({
		uri: `${process.env.NEXT_PUBLIC_MONTHLY_API_URL}/graphql`,
	});

	const httpLinkV1Public = createHttpLink({
		uri: `${process.env.NEXT_PUBLIC_MONTHLY_API_URL}/graphqlPublic`,
	});

	const httpLinkV2 = createHttpLink({
		uri: `${process.env.NEXT_PUBLIC_MONTHLY_API_V2_URL}/graphql`,
	});

	const httpLinkV2Public = createHttpLink({
		uri: `${process.env.NEXT_PUBLIC_MONTHLY_API_V2_URL}/graphqlPublic`,
	});

	const authLink = new ApolloLink(
		(operation, forward) =>
			new Observable((observer) => {
				const { appName, appHeaders } = operation.getContext();
				const { cookies } = context;
				let idToken: string | null = null;

				if (Array.isArray(cookies) && cookies.length) {
					idToken = cookies.find((cookie: Cookie) => cookie.name === 'aws_cognito_id_token')?.value;
				} else {
					idToken = cookies.aws_cognito_id_token;
				}

				const setHeadersAndForward = (token: string | null) => {
					operation.setContext(({ headers = {} }) => ({
						headers: {
							...headers,
							...appHeaders,
							Authorization: token ? `Bearer ${token}` : '',
							AppName: appName || 'ADMIN_DASHBOARD',
						},
					}));
					// Forward the operation to the next link
					const observable = forward(operation);
					observable.subscribe({
						next: observer.next.bind(observer),
						error: observer.error.bind(observer),
						complete: observer.complete.bind(observer),
					});
				};

				if (!idToken) {
					fetchAuthSession({ forceRefresh: true })
						.then(({ tokens }) => {
							const token = tokens?.idToken?.toString() || null;
							setHeadersAndForward(token);
						})
						.catch(observer.error.bind(observer));
				} else {
					setHeadersAndForward(idToken);
				}
			}),
	);

	const publicLink = setContext(async (_, { headers }) => ({
		headers: {
			...headers,
		},
	}));

	return new ApolloClient({
		ssrMode: ssr,
		link: from([
			ApolloLink.split((operation) => operation.getContext()?.public === true, publicLink, authLink),
			removeTypenameLink,
			ApolloLink.split(
				(operation) => operation.getContext()?.public === true,
				// Public routes
				ApolloLink.split(
					(operation) => operation.getContext()?.apiVersion === 'v2',
					from([errorLink, httpLinkV2Public]),
					from([errorLink, httpLinkV1Public]),
				),
				// Auth routes
				ApolloLink.split(
					(operation) => operation.getContext()?.apiVersion === 'v2',
					from([errorLink, httpLinkV2]),
					from([errorLink, httpLinkV1]),
				),
			),
		]),
		cache: new InMemoryCache(),
		ssrForceFetchDelay: 100,
		credentials: 'include',
	});
};

export const useQuery = useApolloQuery;
export const gql = gqlFN;
export const useLazyQuery = useLazyQueryFn;
export const useSuspenseQuery = useSuspenseQueryFn;
export const useMutation = useMutationFn;
export const NetworkStatus = ApolloNetworkStatus;
export type ApolloClientType = ApolloClient<NormalizedCacheObject>;
